import React, { memo, useMemo } from "react";
import { Container, Row, Col, Button, Spinner, Badge } from "react-bootstrap";
import { motion } from "framer-motion";
import { FaArrowRight, FaChevronRight, FaGraduationCap, FaAward, FaUniversity, FaExternalLinkAlt } from "react-icons/fa";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";

// Components
import Header from "../components/Header";
import Footer from "../components/Footer";
import TimelineCarousel from "../components/TimelineCarousel";
import useProgramData from "../hooks/useProgramData";

// Assets
import videoSrc from "../assets/video.mp4";
import campus1 from "../assets/campus-images/kandhkot-campus.jpg";
import campus2 from "../assets/campus-images/dadu-campus.jpg";
import campus3 from "../assets/campus-images/khairpur-campus.jpg";
import campus4 from "../assets/campus-images/mirpur-khas campus.jpg";
import lifeAtCampusImage from "../assets/LIFE_AT_CAMPUS.png";

const Homepage = () => {
  const { data: apiData, loading, error, refetch } = useProgramData("homePageData");
  const data = useMemo(() => apiData?.[0] || null, [apiData]);

  if (loading) return (
    <div className="premium-loader-wrap">
      <div className="loader-inner">
        <Spinner animation="border" variant="primary" />
        <h2 className="loader-branding">SUKKUR IBA</h2>
      </div>
    </div>
  );

  if (error) return (
    <div className="error-viewport">
      <div className="error-glass-card">
        <h3 className="text-danger">Service Interrupted</h3>
        <p>{error}</p>
        <Button variant="primary" onClick={refetch}>Retry Connection</Button>
      </div>
    </div>
  );

  if (!data) return null;

  return (
    <div className="master-site-container bg-white">
      <Header />
      
      {/* --- HERO SECTION --- */}
      <section className="hero-stack bg-light">
        <TimelineCarousel />
        
        <Container className="video-section-spacer">
          <motion.div 
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="video-cinema-frame shadow-2xl"
          >
            <div className="video-wrapper">
              <video src={videoSrc} autoPlay loop muted playsInline className="video-element" />
              <div className="video-ui-overlay">
                <div className="video-top-bar">
                  <Badge bg="primary" className="est-badge">Est. 1994</Badge>
                </div>
                <div className="video-bottom-info">
                  <div className="info-content">
                    <span className="live-tag"><span className="dot" /> Campus View</span>
                    <h4 className="m-0 fw-bold text-white">Shaping Excellence for the Future</h4>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* --- EDITORIAL & ADMISSIONS --- */}
      <section className="portal-editorial py-lg-10 py-7">
        <Container>
          <Row className="g-lg-6 align-items-stretch">
            <Col lg={8} className="editorial-main">
              <motion.div 
                initial={{ opacity: 0 }} 
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="editorial-content-box"
              >
                <div className="category-tag">University News</div>
                <h2 className="portal-heading">{data.admissions.title}</h2>
                <div className="ivy-divider" />
                <div 
                  className="editorial-text-body"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.admissions.description) }}
                />
              </motion.div>
            </Col>
            
            <Col lg={4} className="editorial-sidebar">
              <motion.div 
                whileHover={{ y: -5 }}
                className="admission-action-card"
              >
                <div className="card-accent-top" />
                <div className="p-4">
                  <div className="icon-circle shadow-sm">
                    <FaGraduationCap />
                  </div>
                  <h4 className="fw-bold text-navy mt-4">Admissions Open</h4>
                  <p className="small text-muted mb-4">Fall Semester 2026 application portal is now accepting submissions from eligible candidates.</p>
                  
                  <div className="d-grid gap-2">
                    <Button 
                      href="https://applyadmission.iba-suk.edu.pk"
                      className="btn-portal-primary"
                    >
                      Online Portal <FaExternalLinkAlt className="ms-2 small" />
                    </Button>
                    <Link to="/admission-procedure" className="btn btn-link text-decoration-none text-navy fw-bold small">
                      View Procedure <FaArrowRight className="ms-1" />
                    </Link>
                  </div>
                </div>
                <div className="card-footer-stats">
                  <div className="stat-item"><span>Scholarships</span><strong>Fully Funded</strong></div>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* --- CAMPUS GRID --- */}
      <section className="campus-strip py-9 bg-soft">
        <Container>
          <div className="section-title-area mb-5">
            <h2 className="fw-900 text-navy h1 m-0">University <span className="text-primary">Campuses</span></h2>
            <p className="text-muted">Expanding the horizon of quality education across the region.</p>
          </div>
          <Row className="g-4">
            {[
              { src: campus1, title: "Kandhkot Campus", link: "#" },
              { src: campus2, title: "Dadu Campus", link: "#" },
              { src: campus3, title: "Khairpur Campus", link: "#" },
              { src: campus4, title: "Mirpurkhas Campus", link: "#" },
            ].map((campus, idx) => (
              <Col lg={3} sm={6} key={idx}>
                <motion.a 
                  href={campus.link}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  className="campus-clean-card"
                >
                  <div className="img-frame">
                    <img src={campus.src} alt={campus.title} />
                  </div>
                  <div className="label-area">
                    <span>{campus.title}</span>
                    <FaChevronRight className="icon" />
                  </div>
                </motion.a>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* --- PROGRAM HUB --- */}
      <section className="program-hub-section section-standard-spacing">
        <Container>
          <div className="hub-container shadow-xl">
            <Row className="g-0">
              <Col lg={4} className="hub-nav-col">
                {[
                  { name: "Undergraduate", icon: <FaUniversity />, path: "/programs" },
                  { name: "Graduate", icon: <FaAward />, path: "/programs" },
                  { name: "Scholarships", icon: <FaGraduationCap />, path: "/scholarships" }
                ].map((item, i) => (
                  <Link to={item.path} key={i} className="hub-tab">
                    <span className="tab-icon">{item.icon}</span>
                    <span className="tab-text">{item.name}</span>
                  </Link>
                ))}
              </Col>
              <Col lg={8} className="hub-content-col p-5">
                <div className="slogan-wrapper">
                  <h3 className="fw-900 text-navy mb-3">{data.slogan.title}</h3>
                  <div className="slogan-text" dangerouslySetInnerHTML={{ __html: data.slogan.description }} />
                  <Link to="/admission-procedure" className="hub-cta-link mt-4">
                    Explore Admissions <FaArrowRight className="ms-2" />
                  </Link>
                </div>
              </Col>
            </Row>
          </div>
        </Container>
      </section>

      <section className="life-full-width section-standard-spacing mb-5">
        <Link to="/life-at-campus" className="life-banner">
          <img src={lifeAtCampusImage} alt="Life" />
          <div className="banner-overlay">
            <Container>
              <div className="banner-content text-center text-lg-start">
                <h2 className="display-4 fw-900 mb-2">Life at Sukkur IBA</h2>
                <p className="lead mb-4">Experience the vibrant community and world-class facilities.</p>
                <span className="banner-btn">See Showcase</span>
              </div>
            </Container>
          </div>
        </Link>
      </section>

      <Footer />

      <style>{`
        /* Core Layout */
        .text-navy { color: #002b5a; }
        .bg-soft { background: #f8fafc; }
        .fw-900 { font-weight: 900; }
        .py-10 { padding: 6rem 0; }
        .py-9 { padding: 5rem 0; }
        
        /* Uniform Section Spacing Strategy */
        .section-standard-spacing {
          margin-bottom: 80px;
        }

        /* Video Section Engineering */
        .video-section-spacer { margin-top: 40px; padding-bottom: 60px; }
        .video-cinema-frame { border-radius: 24px; overflow: hidden; background: #000; border: 8px solid white; }
        .video-wrapper { position: relative; aspect-ratio: 21 / 9; min-height: 400px; }
        .video-element { width: 100%; height: 100%; object-fit: cover; opacity: 0.9; }
        
        .video-ui-overlay { position: absolute; inset: 0; display: flex; flex-direction: column; justify-content: space-between; padding: 25px; background: linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, transparent 40%, rgba(0,0,0,0.7) 100%); }
        .est-badge { padding: 8px 16px; border-radius: 50px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; }
        
        .video-bottom-info { display: flex; align-items: flex-end; }
        .live-tag { display: flex; align-items: center; font-size: 0.75rem; text-transform: uppercase; color: #fff; letter-spacing: 2px; margin-bottom: 5px; opacity: 0.8; }
        .dot { height: 8px; width: 8px; background: #ff4d4d; border-radius: 50%; display: inline-block; margin-right: 8px; animation: pulse 1.5s infinite; }

        /* Editorial Section engineering */
        .portal-editorial { background: #fff; position: relative; }
        .category-tag { color: #0055a4; font-weight: 800; text-transform: uppercase; font-size: 0.85rem; letter-spacing: 2px; margin-bottom: 10px; }
        .portal-heading { font-weight: 900; color: #002b5a; font-size: 2.8rem; margin-bottom: 20px; }
        .ivy-divider { width: 80px; height: 5px; background: #0055a4; margin-bottom: 30px; }
        .editorial-text-body { font-family: 'Inter', sans-serif; font-size: 1.15rem; line-height: 1.8; color: #475569; }

        /* Sidebar Admissions Card */
        .admission-action-card { background: #fdfdfd; border-radius: 20px; border: 1px solid #e2e8f0; position: relative; overflow: hidden; }
        .card-accent-top { height: 6px; background: #0055a4; }
        .icon-circle { width: 60px; height: 60px; background: #002b5a; color: #fff; border-radius: 15px; display: flex; align-items: center; justify-content: center; font-size: 1.5rem; }
        .btn-portal-primary { background: #002b5a; color: #fff; border: none; padding: 12px; border-radius: 10px; font-weight: 700; transition: 0.3s; }
        .btn-portal-primary:hover { background: #0055a4; transform: scale(1.02); }
        .card-footer-stats { background: #f1f5f9; padding: 15px 25px; border-top: 1px solid #e2e8f0; }
        .stat-item { display: flex; justify-content: space-between; align-items: center; font-size: 0.85rem; }
        .stat-item strong { color: #059669; }

        /* Campus Cards */
        .campus-clean-card { display: block; text-decoration: none !important; color: inherit; }
        .img-frame { border-radius: 16px; overflow: hidden; height: 220px; margin-bottom: 15px; }
        .img-frame img { width: 100%; height: 100%; object-fit: cover; transition: 0.5s; }
        .label-area { display: flex; justify-content: space-between; align-items: center; font-weight: 700; color: #002b5a; }
        .campus-clean-card:hover img { transform: scale(1.1); }
        .campus-clean-card:hover .label-area { color: #0055a4; }

        /* Program Hub */
        .hub-container { background: #fff; border-radius: 30px; overflow: hidden; border: 1px solid #f1f5f9; }
        .hub-nav-col { background: #002b5a; display: flex; flex-direction: column; }
        .hub-tab { padding: 30px; color: #fff; text-decoration: none !important; border-bottom: 1px solid rgba(255,255,255,0.1); display: flex; align-items: center; transition: 0.3s; }
        .hub-tab:hover { background: rgba(255,255,255,0.05); }
        .tab-icon { margin-right: 20px; font-size: 1.5rem; color: #ffcc00; }
        .tab-text { font-weight: 700; text-transform: uppercase; letter-spacing: 1px; }
        .slogan-text { font-family: 'Georgia', serif; font-style: italic; color: #64748b; font-size: 1.2rem; line-height: 1.6; }
        .hub-cta-link { display: inline-block; font-weight: 800; color: #0055a4; text-decoration: none !important; }

        /* Life Banner */
        .life-banner { position: relative; height: 600px; display: block; overflow: hidden; }
        .life-banner img { width: 100%; height: 100%; object-fit: cover; transition: 2s; }
        .banner-overlay { position: absolute; inset: 0; background: rgba(0, 43, 90, 0.6); display: flex; align-items: center; color: #fff; }
        .banner-btn { display: inline-block; margin-top: 30px; padding: 15px 40px; border: 2px solid #fff; border-radius: 50px; font-weight: 700; }
        .life-banner:hover img { transform: scale(1.1); }

        @keyframes pulse { 0% { opacity: 1; } 50% { opacity: 0.5; } 100% { opacity: 1; } }


        @media (max-width: 991px) {
          /* General Section Padding - Reduced for Mobile */
          .py-10, .py-9, .py-7, .py-lg-10 { padding: 50px 0 !important; }
          .mt-5 { margin-top: 40px !important; }
          .section-standard-spacing { margin-bottom: 50px; }

          /* Program Hub - Stacked & Compact */
          .hub-nav-col { flex-direction: row; flex-wrap: wrap; }
          .hub-tab { flex: 1; padding: 15px; font-size: 0.8rem; border-right: 1px solid rgba(255,255,255,0.1); justify-content: center; }
          .hub-content-col { padding: 25px !important; }
          .slogan-text { font-size: 1rem; line-height: 1.5; }
          .tab-icon { margin-right: 10px; font-size: 1.2rem; }

          /* Editorial Section */
          .editorial-main { margin-bottom: 30px; }
          .portal-heading { font-size: 2rem; margin-bottom: 15px; }
          .editorial-text-body { font-size: 0.95rem; line-height: 1.6; }
          .ivy-divider { margin-bottom: 20px; }
          .admissions-sidebar-col { padding-top: 20px; }

          /* Campus Grid */
          .img-frame { height: 160px; }
          .label-area { font-size: 0.9rem; }
          
          /* Video Section Mobile */
          .video-section-spacer { margin-top: 15px; padding-bottom: 30px; }
          .video-cinema-frame { border-radius: 16px; border: 4px solid white; }
          .video-wrapper { aspect-ratio: 16 / 9; min-height: 250px; }
          .video-ui-overlay { padding: 15px; }
          .est-badge { padding: 5px 12px; font-size: 0.7rem; }
          .video-bottom-info h4 { font-size: 1.1rem !important; }
          .live-tag { font-size: 0.65rem; }
          
          /* Life at Campus Mobile */
          .life-banner { height: 350px; }
          .banner-content h2 { font-size: 1.8rem !important; }
          .banner-content p { font-size: 0.95rem !important; }
          .banner-btn { padding: 10px 25px; font-size: 0.85rem; margin-top: 15px; }
        }

        @media (max-width: 576px) {
          /* Further Compactness for Small Mobile */
          .py-10, .py-9, .py-7, .py-lg-10 { padding: 40px 0 !important; }
          
          /* Campus Grid - ensure breathing room if stacked, or tight 2-col */
          .img-frame { height: 140px; }
          
          /* Editorial */
          .portal-heading { font-size: 1.7rem; }
          .editorial-text-body { font-size: 0.9rem; }

          /* Video Section Extra Small */
          .video-section-spacer { margin-top: 10px; padding-bottom: 20px; }
          .video-cinema-frame { border-radius: 12px; border: 3px solid white; }
          .video-wrapper { min-height: 200px; }
          .video-ui-overlay { padding: 12px; }
          .est-badge { padding: 4px 10px; font-size: 0.65rem; }
          .video-bottom-info h4 { font-size: 0.95rem !important; }
          .live-tag { font-size: 0.6rem; margin-bottom: 3px; }
          .dot { height: 6px; width: 6px; }
          
          /* Life at Campus Extra Small */
          .life-banner { height: 280px; }
          .banner-content h2 { font-size: 1.4rem !important; }
          .banner-content p { font-size: 0.85rem !important; margin-bottom: 0.5rem !important; }
          .banner-btn { padding: 8px 20px; font-size: 0.75rem; margin-top: 10px; }
        }
      `}</style>
    </div>
  );
};

export default memo(Homepage);